import logger from 'src/logger';
import { htmlComponentType, makeHTMLComponent } from 'src/sdk-components';
import ThreeInstance from 'src/sdk-components/ThreeInstance';
import { TourHTML, TourImage, VSInteractionType } from 'src/sdk-components/VSObject';
import { SceneMode } from 'src/service/scene/scene-nodes/SceneImage/types';
import { TransformationMode } from 'src/service/scene/scene-nodes/TransformControls/data';
import { LoadingState, TSceneNode } from 'src/service/scene/types';
import { SDKConfig } from 'src/service/types';
import { MpSdk } from 'src/types';
import CSS3DObject from 'src/types/CSS3DObject';
import Scene from '../../scene';
import { NodeEventsTypes } from 'src/service/scene/enums';
import { DeviceManager } from 'src/utils';

class SceneHTML extends Scene {
  readonly mpSDK: any;

  assets = new Array<[any, any[]]>();

  public editMode: boolean;

  public outlineMesh: any;

  constructor(mpSDK: MpSdk, _config: SDKConfig, private transformControls: any) {
    super(mpSDK);
    this.mpSDK = mpSDK;
  }

  async init() {
    await this.mpSDK.Scene.register(htmlComponentType, makeHTMLComponent);
  }

  async create(data: TourHTML): Promise<TSceneNode | undefined> {
    return new Promise(async (resolve) => {
      try {
        const assetNode = this.getAssetNode(data);
        if (assetNode) {
          resolve(assetNode);
          return;
        }
        const THREE = await ThreeInstance.get();
        if (THREE == null) {
          logger.error(`Failed to init THREE: ${data.details.title}.`);
          resolve([] as any);
          return;
        }
        // saving the asset into the array to prevent rendering it few times
        const assetsLength = this.assets.push([data, []]);

        // Add your component to the scene node
        const modelNode: any = await this.scene.createNode();

        const component = modelNode.addComponent(htmlComponentType, {
          url: data.details.url ?? '',
          enableAnimation: data.enableAnimation ?? false,
          enableHoverAnimation: data.enableHoverAnimation ?? false,
          animationPulse: data.animationPulse ?? false,
          animationUpDown: data.animationUpDown ?? false,
          animationFlip: data.animationFlip ?? false
        });

        modelNode.position.set(data.positionX, data.positionY, data.positionZ);
        modelNode.quaternion.setFromEuler(
          new THREE.Euler(data.rotationX, data.rotationY, data.rotationZ)
        );
        modelNode.scale.set(data.scaleX, data.scaleY, data.scaleZ);

        modelNode.start();

        let object;
        modelNode.obj3D.traverse((obj) => {
          if (obj.isCSS3DObject) object = obj;
        });

        const blendingMesh = this.addBlendingMesh(modelNode.obj3D, object, THREE);
        if (DeviceManager.isMobile() || data.isFlowsHtml)
          this.addOutlineMesh(blendingMesh, object, THREE);
        component.outputs.collider = blendingMesh;

        component.onEvent = async (type: string, eventData: any) => {
          if (type === VSInteractionType.CLICK) {
            this.emit(NodeEventsTypes.HTML_CLICK, { assetId: data.id, eventData, data });
          }
          if (type === VSInteractionType.HOVER) {
            if (eventData.hover && !this.editMode) component.rendererElement.style.zIndex = 1;
            else if (!DeviceManager.isMobile()) component.rendererElement.style.zIndex = 0;

            if (this.outlineMesh) this.outlineMesh.visible = true;
            this.emit(NodeEventsTypes.HTML_HOVER, { assetId: data.id, eventData, data });
          }
        };

        component.outputs.onPropertyChanged('loadingState', async (state: any) => {
          if (state === LoadingState.Loaded) {
            this.assets[assetsLength - 1] = [data, [modelNode, component]];

            resolve([modelNode, component]);
          } else if (state === LoadingState.Error) {
            resolve([] as any);
          }
        });
        if (DeviceManager.isMobile() || data.isFlowsHtml)
          this.mpSDK.Camera.pose.subscribe(() => {
            component.rendererElement.style.zIndex = 0;
            if (this.outlineMesh) this.outlineMesh.visible = false;
          });
      } catch (error) {
        logger.error(`Failed to init HTML: ${data.details.title}.`, error);
        resolve([] as any);
      }
    });
  }

  addBlendingMesh(rootObject, cssObject, THREE) {
    const width = parseInt(cssObject.element.style.width.match(/\d/g).join(''), 10);
    const height = parseInt(cssObject.element.style.height.match(/\d/g).join(''), 10);
    const vec3 = new THREE.Vector3();
    rootObject.getWorldPosition(vec3);

    const material = new THREE.MeshBasicMaterial({
      color: 'black',
      blending: THREE.NoBlending,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide
    });
    const geometry = new THREE.PlaneGeometry(width, height);
    const mesh = new THREE.Mesh(geometry, material);

    cssObject.add(mesh);
    return mesh;
  }

  addOutlineMesh(blendingMesh, cssObject, THREE) {
    const material = new THREE.MeshBasicMaterial({
      color: 'yellow',
      blending: THREE.NoBlending,
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide
    });
    const geometry = new THREE.PlaneGeometry(
      blendingMesh.geometry.parameters.width,
      blendingMesh.geometry.parameters.height
    );
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.z = -0.02;
    mesh.scale.setScalar(1.025);
    mesh.visible = false;
    this.outlineMesh = mesh;

    cssObject.add(mesh);
    return mesh;
  }

  remove(data: TourImage) {
    const node = this.getAssetNode(data);
    if (node?.length === 2) {
      node[1].forEach((el) => {
        if (el && el.obj3D) {
          el.stop();
          el.obj3D.remove(el.obj3D.children[0]);
          this.assets = this.filterAssetOut(data);
        }
      });
    }
  }

  async changeMode(
    mode: SceneMode,
    node: TSceneNode,
    transformationMode: TransformationMode = TransformationMode.TRANSLATE
  ) {
    const [modelNode, component] = node;
    this.setNodeEditView(mode === SceneMode.Edit, component);

    if (mode === SceneMode.Edit) {
      this.editMode = true;
      // Make the transform control visible so that the user can manipulate the control selection.
      this.transformControls.control.outputs.objectRoot.visible = true;
      // The three.js object being controlled by this component.
      this.transformControls.control.inputs.selection = modelNode;
      // The transformation mode. Valid values are ‘translate’, ‘rotate’ and ‘scale’.
      this.transformControls.control.inputs.mode = transformationMode;
    } else {
      this.editMode = false;
      this.transformControls.control.outputs.objectRoot.visible = false;
      this.transformControls.control.inputs.selection = null;
      this.transformControls.control.inputs.mode = transformationMode;
    }
  }

  setNodeEditView = (_state = false, component: any) => {
    if (!component) return;
    if (component.outputs.loadingState !== LoadingState.Loaded) return;

    component.outputs.objectRoot.traverse((obj: CSS3DObject) => {
      if (obj.children?.length) return;
      if (obj.element) {
        const iframe = obj.element;
        iframe.style.pointerEvents = 'none';
      }
    });
  };

  getAssetNode = (asset: any): TSceneNode | null => {
    let node: any = null;
    node = this.assets.find(
      ([a]) => (a.id && a.id === asset.id) || (a.uuid === asset.uuid && a.uuid && asset.uuid)
    );
    return node;
  };

  filterAssetOut = (asset: any): Array<[any, any[]]> => {
    return this.assets.filter(
      ([a]) => !((a.id && a.id === asset.id) || (a.uuid === asset.uuid && a.uuid && asset.uuid))
    );
  };
}

export default SceneHTML;
